/**
 * Types and pure helpers for the Manage Referee Assignment page.
 * Kept free of Angular dependencies so the rules are easy to unit test.
 */

/** One referee slot as sent to / received from the API. */
export interface RefereeRow {
  id?: number | string;
  name: string | null;
  email: string | null;
  phone: string | null;
  user_id: number | string | null;
  /** Optional server-side status; used as-is when present. */
  status?: string | null;
  [key: string]: any;
}

export type RefereeField = 'name' | 'email' | 'phone';

/** Field-level error messages for a single row. */
export type RowErrors = Partial<Record<RefereeField, string>>;

/**
 * Status values returned by the backend (field_event_referee_contact_assigned.status):
 * pending -> completed (sub-referee accepted) | rejected (sub-referee rejected).
 * Unknown values fall back to a neutral badge.
 */
export type RefereeStatusKey = 'pending' | 'completed' | 'accepted_not_joined' | 'rejected' | string;

/** Display-only status: accepted (completed) but no PANDA Stronger account yet (backend has_account = false). */
export const ACCEPTED_NOT_JOINED = 'accepted_not_joined';

export interface RefereeStatus {
  key: RefereeStatusKey;
  label: string;
  /** Tailwind classes for the badge. */
  css: string;
}

/** Badge styles per known status. */
// Panda theme: primary palette (tailwind primary-*), red outline for rejected
// to match the Reject buttons on the contract pages.
const STATUS_STYLES: Record<string, string> = {
  pending: 'bg-primary-light text-primary-shade',
  completed: 'bg-primary-base text-primary-text',
  accepted_not_joined: 'bg-white text-primary-shade ring-1 ring-primary-base',
  rejected: 'bg-transparent text-red-600 ring-1 ring-red-600',
};

/** Status for rows not saved yet (new rows have no backend status). */
export const DEFAULT_REFEREE_STATUS: RefereeStatusKey = 'pending';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const PHONE_DIGITS = 10;

export const emptyRefereeRow = (): RefereeRow => ({
  name: null,
  email: null,
  phone: null,
  user_id: null,
});

export const normalizeEmail = (email: string | null | undefined): string =>
  (email || '').trim().toLowerCase();

/** Strips mask characters so "(555)123-4567" and "5551234567" compare equal. */
export const phoneDigits = (phone: string | number | null | undefined): string =>
  String(phone ?? '').replace(/\D/g, '');

/** A row the assigner has not touched at all; skipped on save and in validation. */
export const isRowEmpty = (row: RefereeRow): boolean =>
  !row.name?.trim() && !normalizeEmail(row.email) && !phoneDigits(row.phone);

/**
 * Validates every non-empty row.
 * Rules: name, email and phone are required; email must be well formed;
 * phone must have 10 digits; the same email may not be used on two rows.
 * Returns a map of row index -> field errors (only rows with errors appear).
 */
export function validateRefereeRows(rows: RefereeRow[]): Record<number, RowErrors> {
  const errors: Record<number, RowErrors> = {};
  const firstIndexByEmail = new Map<string, number>();

  rows.forEach((row, index) => {
    if (isRowEmpty(row)) {
      return;
    }
    const rowErrors: RowErrors = {};
    const email = normalizeEmail(row.email);
    const phone = phoneDigits(row.phone);

    if (!row.name?.trim()) {
      rowErrors.name = 'Referee name is required.';
    }

    if (!email) {
      rowErrors.email = 'Email is required.';
    } else if (!EMAIL_PATTERN.test(email)) {
      rowErrors.email = 'Enter a valid email address.';
    } else if (firstIndexByEmail.has(email)) {
      rowErrors.email = `A referee with email ${email} already exists (row ${firstIndexByEmail.get(email) + 1}).`;
    } else {
      firstIndexByEmail.set(email, index);
    }

    if (!phone) {
      rowErrors.phone = 'Mobile number is required.';
    } else if (phone.length !== PHONE_DIGITS) {
      rowErrors.phone = 'Enter a 10-digit mobile number.';
    }

    if (Object.keys(rowErrors).length) {
      errors[index] = rowErrors;
    }
  });

  return errors;
}

/**
 * Status badge for a row. Uses the backend `status` when present
 * (e.g. "pending", "completed"); otherwise defaults to Pending.
 */
export function getRefereeStatus(row: RefereeRow): RefereeStatus {
  let key = (row?.status || DEFAULT_REFEREE_STATUS).toString().trim().toLowerCase();
  // Accepted, but the referee has not created a PANDA Stronger account yet.
  if (key === 'completed' && row?.has_account === false) {
    key = ACCEPTED_NOT_JOINED;
  }
  // Display labels only; backend status values are unchanged ('completed' shows as "Accepted").
  const label = key === ACCEPTED_NOT_JOINED
    ? 'Accepted - Not Joined'
    : key === 'completed'
      ? 'Accepted'
      : key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ');
  return { key, label, css: STATUS_STYLES[key] || 'bg-gray-100 text-gray-600' };
}

/**
 * A saved row the sub-referee has already answered (accepted or rejected).
 * These are locked: the backend matches rows by email, so editing one would
 * create a new pending row instead of changing the answered one.
 */
export function isRowLocked(row: RefereeRow): boolean {
  const key = getRefereeStatus(row).key;
  return !!row?.id && (isAcceptedKey(key) || key === 'rejected');
}

/** Every row can be removed, including referees who already accepted. */
export function canDeleteRow(_row: RefereeRow): boolean {
  return true;
}

/** Row is a saved referee who already accepted (joined or not). */
export function isAcceptedRow(row: RefereeRow): boolean {
  return !!row?.id && isAcceptedKey(getRefereeStatus(row).key);
}

/** Both accepted variants (joined or not) count as accepted for locking and delete rules. */
function isAcceptedKey(key: string): boolean {
  return key === 'completed' || key === ACCEPTED_NOT_JOINED;
}
